import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Clock, CheckCircle, XCircle, HelpCircle } from 'lucide-react';
import { DAILY_QUIZ } from '@/data/dailyQuiz';
import { completeMission } from '@/services/pointsService';
import { toast } from 'sonner';

interface DailyQuizModalProps {
    isOpen: boolean;
    onClose: () => void;
    onComplete: () => void;
    missionId: string;
    playerId: string;
}

export function DailyQuizModal({ isOpen, onClose, onComplete, missionId, playerId }: DailyQuizModalProps) {
    const [currentQuestion, setCurrentQuestion] = useState<typeof DAILY_QUIZ[0] | null>(null);
    const [shuffledAnswers, setShuffledAnswers] = useState<{ text: string; originalIndex: number }[]>([]);
    const [timeLeft, setTimeLeft] = useState(30);
    const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
    const [status, setStatus] = useState<'idle' | 'playing' | 'success' | 'failed'>('idle');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const getDayOfYear = () => {
        const now = new Date();
        // Use UTC components to align with mission reset (midnight UTC)
        const start = new Date(Date.UTC(now.getUTCFullYear(), 0, 0));
        const diff = now.getTime() - start.getTime();
        const oneDay = 1000 * 60 * 60 * 24;
        return Math.floor(diff / oneDay);
    };

    // Initialize quiz
    useEffect(() => {
        if (isOpen && status === 'idle') {
            const dayOfYear = getDayOfYear();
            // Use modulo to cycle through questions if we run out
            const questionIndex = dayOfYear % DAILY_QUIZ.length;
            const question = DAILY_QUIZ[questionIndex];

            setCurrentQuestion(question);

            // Shuffle answers but keep track of original indices to check correctness
            const answers = question.possibilities.map((text, index) => ({
                text,
                originalIndex: index
            }));

            // Fisher-Yates shuffle
            for (let i = answers.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1));
                [answers[i], answers[j]] = [answers[j], answers[i]];
            }

            setShuffledAnswers(answers);

            // Timer persistence logic
            const storageKey = `daily_quiz_start_${playerId}_${dayOfYear}`;
            const storedStart = localStorage.getItem(storageKey);
            let newTimeLeft = 30;

            if (storedStart) {
                const startTime = parseInt(storedStart, 10);
                const elapsed = Math.floor((Date.now() - startTime) / 1000);

                if (elapsed >= 30) {
                    setStatus('failed');
                    setTimeLeft(0);
                    // Record failure as completed mission with 0 points if not already done
                    completeMission(playerId, missionId, 0).then(() => {
                        onComplete();
                    }).catch(console.error);
                    return;
                } else {
                    newTimeLeft = Math.max(0, 30 - elapsed);
                }
            } else {
                localStorage.setItem(storageKey, Date.now().toString());
            }

            setStatus('playing');
            setTimeLeft(newTimeLeft);
        }
    }, [isOpen, status, playerId]);

    // Timer logic
    useEffect(() => {
        if (status !== 'playing') return;

        const timer = setInterval(async () => {
            setTimeLeft((prev) => {
                if (prev <= 1) {
                    setStatus('failed');
                    // Record failure as completed mission with 0 points
                    completeMission(playerId, missionId, 0)
                        .catch(err => console.error('Failed to record quiz failure', err));
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [status, playerId, missionId]);

    const handleAnswer = async (originalIndex: number) => {
        if (status !== 'playing' || isSubmitting) return;

        setSelectedAnswer(originalIndex);
        setIsSubmitting(true);

        if (currentQuestion && originalIndex === currentQuestion.answer) {
            // Correct answer
            try {
                await completeMission(playerId, missionId);
                setStatus('success');
                toast.success('Correct answer! Mission completed.');
                setTimeout(() => {
                    onComplete();
                    handleClose();
                }, 2000);
            } catch (error) {
                console.error('Error completing mission:', error);
                toast.error('Error validating mission');
                setStatus('playing'); // Let them try again? Or fail? Usually fail if error but let's assume retryable
            }
        } else {
            // Wrong answer
            setStatus('failed');
            // Record failure as completed mission with 0 points
            try {
                await completeMission(playerId, missionId, 0);
                onComplete(); // Refresh parent data
            } catch (error) {
                console.error('Error recording failed mission:', error);
            }
        }
        setIsSubmitting(false);
    };

    const handleClose = () => {
        onClose();
        // Reset state after a short delay to allow animation to close
        setTimeout(() => {
            setStatus('idle');
            setSelectedAnswer(null);
            setTimeLeft(30);
        }, 300);
    };

    if (!currentQuestion) return null;

    return (
        <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
            <DialogContent className="sm:max-w-md rounded-custom-paddings ">
                <DialogHeader className="px-4 pt-4">
                    <DialogTitle className="flex items-center gap-2">
                        <HelpCircle className="w-5 h-5 text-primary" />
                        Daily Indie Quiz
                    </DialogTitle>
                    <DialogDescription>
                        Answer correctly to complete the daily mission!
                    </DialogDescription>
                </DialogHeader>

                <div className="py-4 px-4 space-y-6">
                    {/* Timer */}
                    <div className="space-y-2">
                        <div className="flex justify-between text-sm font-medium">
                            <span className={timeLeft < 10 ? "text-red-500" : "text-muted-foreground"}>
                                Time remaining
                            </span>
                            <span className={timeLeft < 10 ? "text-red-500" : ""}>
                                {timeLeft}s
                            </span>
                        </div>
                        <Progress value={(timeLeft / 30) * 100} className={timeLeft < 10 ? "bg-red-100 [&>div]:bg-red-500" : ""} />
                    </div>

                    {/* Question */}
                    <div className="bg-muted/50 p-4 rounded-lg">
                        <p className="text-lg font-medium text-center">{currentQuestion.question}</p>
                    </div>

                    {/* Answers */}
                    <div className="grid grid-cols-1 gap-3">
                        {shuffledAnswers.map((answer, index) => {
                            let variant: "outline" | "default" | "destructive" | "secondary" = "outline";
                            let className = "justify-start h-auto py-3 px-4 text-left";

                            if (status === 'success' && answer.originalIndex === currentQuestion.answer) {
                                variant = "default";
                                className += " bg-green-500 hover:bg-green-600 border-green-600";
                            } else if (status === 'failed' && selectedAnswer === answer.originalIndex) {
                                variant = "destructive";
                            } else if (status === 'failed' && answer.originalIndex === currentQuestion.answer) {
                                // Show correct answer even if failed
                                variant = "outline";
                                className += " border-green-500 text-green-600 bg-green-50";
                            }

                            return (
                                <Button
                                    key={index}
                                    variant={variant}
                                    className={className}
                                    onClick={() => handleAnswer(answer.originalIndex)}
                                    disabled={status !== 'playing' || isSubmitting}
                                >
                                    <span className="mr-2 opacity-70">{String.fromCharCode(65 + index)}.</span>
                                    {answer.text}
                                </Button>
                            );
                        })}
                    </div>

                    {/* Status Messages */}
                    {status === 'success' && (
                        <div className="flex items-center justify-center gap-2 text-green-600 font-medium animate-in fade-in zoom-in duration-300">
                            <CheckCircle className="w-5 h-5" />
                            <span>Correct! +4 points</span>
                        </div>
                    )}

                    {status === 'failed' && (
                        <div className="flex items-center justify-center gap-2 text-red-500 font-medium animate-in fade-in zoom-in duration-300">
                            <XCircle className="w-5 h-5" />
                            <span>Time's up or wrong answer! Try again tomorrow.</span>
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}
